import { createDraftSafeSelector } from "@reduxjs/toolkit";
import { PRIMARY_TRADING_ACCOUNT_KEY } from "../assets/constants";
import { normalizePlatform } from '../components/widgetDashboard/widgetDashboard.helper';
import { Broker, TradingAccount, TradingAccountMargin } from "../proto/generated/trading-bridge";
import { DashboardNamespace, Platform, UserDashboardProto } from '../proto/generated/user-preference';
import { PayOffSettings, QuickWidgetTypes, UserDetailType, UserStateType } from "../reducer/user.reducer";
import { RootState } from "../store";
// import { SubscriptionPlan, subscriptionPlanFromJSON } from "../proto/generated/subscription";
const state = (state: RootState) => state.userStore as UserStateType


export const selectUserDetails = createDraftSafeSelector(state, (store) => store.userDetails as UserDetailType);
export const selectUserSubscriptionPlans = createDraftSafeSelector(state, (store) => (store.userDetails?.currentUser?.subscriptionPlans));
export const selectUserSubscriptionPlansData = createDraftSafeSelector(state, (store) => store.subscriptionPlans);
export const selectAllFeatures = createDraftSafeSelector(state, (store) => store.allUserFeatures);
export const selectFeatureRestrictionDialog = createDraftSafeSelector(state, (store) => store.featureRestrictionDialog);
export const selectTradingAccounts = createDraftSafeSelector(state, (store) => store.tradingAccounts as TradingAccount[]);
/** False until the first trading-accounts payload; the list is `undefined` before that, not empty. */
export const selectTradingAccountsLoaded = createDraftSafeSelector(state, (store) => store.tradingAccounts !== undefined);
export const selectPrimaryTradingAccount = createDraftSafeSelector(state, (store) => {
    let acc = store.primaryTradingAccount as (TradingAccount | undefined)
    const storageAcc = sessionStorage.getItem(PRIMARY_TRADING_ACCOUNT_KEY)
    if(storageAcc && storageAcc !== "undefined") {
        const parsedAcc = store?.tradingAccounts?.find(t=>t?.accountId===storageAcc)
        if(parsedAcc) {
            acc = parsedAcc
        }
    }
    return acc
})
export const selectBrokers = createDraftSafeSelector(state, (store) => store.brokers as Broker[]);
export const selectMargins = createDraftSafeSelector(state, (store) => store.margins as Record<string, TradingAccountMargin>);
export const selectAutoSave = createDraftSafeSelector(state, (store) => store.userDetails?.preferences?.chart?.autoSave ?? false)
export const watchListView = createDraftSafeSelector(state, (store) => store.userDetails?.preferences?.watchlist?.tableView ?? false)
export const selectCustomizedColumns = createDraftSafeSelector(state, (store) => store.userDetails.preferences.watchlist.customizedColumn)
export const selectNumberOfCustomizedWatchlistColumns = createDraftSafeSelector(state, (store) => {
    let count = 1
    const columns = store.userDetails.preferences.watchlist.customizedColumn
    if (columns.last) count++
    if (columns.change) count++
    if (columns.changePercentage) count++
    if (columns.volume) count++
    return count
})
export const makeSelectQuickModeEnabled = (widgetType: QuickWidgetTypes) => createDraftSafeSelector(state,(store) =>store.userDetails?.preferences?.quickWidgets?.[widgetType]?.QuickmodeEnabled ?? false);
export const selectScreenerAutoSave =  createDraftSafeSelector(state, (store) => store.userDetails?.preferences?.screener?.autoSave??false)
export const selectUserPreferences =  createDraftSafeSelector(state, (store) => store.userDetails.preferences)
// One memoized selector per platform, so callers get a stable selector reference across renders.
const defaultLayoutIdSelectorCache = new Map<Platform, (s: RootState) => string>()
export const selectDefaultLayoutId = (platform: Platform) => {
    let selector = defaultLayoutIdSelectorCache.get(platform)
    if (!selector) {
        selector = createDraftSafeSelector(state, (store) => {
            const chartDashboards = (store.userDashboards ?? []).filter((d: UserDashboardProto) =>
                d.namespace === DashboardNamespace.CHART && normalizePlatform(d.platform) === platform
            );
            return chartDashboards.find((d) => d.isDefault)?.id ?? chartDashboards[0]?.id ?? '';
        })
        defaultLayoutIdSelectorCache.set(platform, selector)
    }
    return selector
}
export const areDashboardsLoaded = createDraftSafeSelector(state, (store) => store.userDashboardsLoaded)
export const selectFavouriteTimeBuckets = createDraftSafeSelector(state, (store) => store.userDetails?.preferences?.favoriteTimeBuckets ?? [])
export const selectFavoriteChartTypes = createDraftSafeSelector(state, (store) => store.userDetails?.preferences?.favoriteChartTypes ?? [])
export const selectCustomTimeBuckets = createDraftSafeSelector(state, (store) => store.userDetails?.preferences?.customTimeBuckets ?? [])
export const selectPayoffPreferences = createDraftSafeSelector(state, (store) => store.userDetails.preferences.payOffSettings ?? {} as PayOffSettings)
export const selectScreenerRefreshDuration = createDraftSafeSelector(state, (store) => store.userDetails?.preferences?.screener?.refreshDuration ?? '')
export const selectScreenerDisplayCompanyName = createDraftSafeSelector(state, (store) => store.userDetails?.preferences?.screener?.displayCompanyName ?? false)
export const selectScreenerDisplayCurrencySymbol = createDraftSafeSelector(state, (store) => store.userDetails?.preferences?.screener?.displayCurrencySymbol ?? false)
export const selectScreenerExchange = createDraftSafeSelector(state, (store) => store.userDetails?.preferences?.screener?.displayExchange ?? false)
export const selectLoginConfiguration = createDraftSafeSelector(state, (store) => store.loginConfiguration)